import { useState } from 'react';
import { BarcodeIcon } from '@phosphor-icons/react';
import { SearchSuggest } from '@skupreme/shared/ui';
import { useDebounce } from '@hooks/debounce';
import { isValidOrderSearch, ordersAutocompleteHook } from '@hooks/order';
import { useIsV2Account } from '@features/page-versions';
import { splitMatches } from '../../lib/highlight';

export type OrderSearchOption = {
  _id: string;
  marketplace_order_display_id?: string;
  fulfillment_status?: string;
};

export type OrderSearchProps = {
  /** Called on every keystroke; suggestions are debounced separately. */
  onQueryChange: (query: string) => void;
  onSelect: (order: OrderSearchOption) => void;
  className?: string;
};

const SUGGEST_DELAY_MS = 1000;
/* Below this the query matches too much to be worth a round trip. */
const SUGGEST_MIN_LENGTH = 3;

export function OrderSearch({
  onQueryChange,
  onSelect,
  className,
}: OrderSearchProps) {
  const [query, setQuery] = useState('');
  const settled = useDebounce(query, SUGGEST_DELAY_MS, SUGGEST_MIN_LENGTH);
  const isV2 = useIsV2Account();
  const { data, loading } = ordersAutocompleteHook(settled, isV2);

  const change = (value: string) => {
    setQuery(value);
    onQueryChange(value);
  };

  /* Rows from an older query are dropped rather than shown as stale results. */
  const isSettled = settled === query && isValidOrderSearch(query);
  const isSearchable = query.trim().length > SUGGEST_MIN_LENGTH;
  const items = isSettled
    ? (data as OrderSearchOption[]).map(order => ({
        ...order,
        id: order._id,
      }))
    : [];

  return (
    <SearchSuggest
      label="Search orders"
      placeholder="Order ID, SKU, fulfillment ID"
      value={query}
      onValueChange={change}
      allowBarcodeScanner
      items={items}
      isLoading={isSearchable && (loading || !isSettled)}
      emptyText={isSearchable ? 'No orders found' : 'Keep typing to search'}
      footer={
        <span className="sk:flex sk:items-center sk:gap-1.5">
          <BarcodeIcon size={14} className="sk:shrink-0" />
          Scan an order barcode to open the order
        </span>
      }
      onSelect={order => {
        onSelect(order);
        change('');
      }}
      className={className}
    >
      {order => (
        <span className="sk:flex sk:items-center sk:justify-between sk:gap-3">
          <span className="sk:min-w-0 sk:truncate sk:font-medium sk:text-fg">
            {splitMatches(order.marketplace_order_display_id, query).map(
              (part, index) =>
                part.isMatch ? (
                  <strong key={index} className="sk:font-semibold">
                    {part.text}
                  </strong>
                ) : (
                  <span key={index}>{part.text}</span>
                ),
            )}
          </span>
          <span className="sk:shrink-0 sk:text-fg-tertiary">
            {order.fulfillment_status}
          </span>
        </span>
      )}
    </SearchSuggest>
  );
}
